'use client';

import styles from './project-detail.module.scss';
import { useContentSize } from "@/components/SizeProvider";
import { formatDesktopWindowTitle } from '@/lib/utils/typography';
import VideoPlayer from "@/components/VideoPlayer";
import DraggableWindow from "@/components/DraggableWindow";
import { useMemo } from "react";
import { ProjectModel } from "@/lib/models/Project";
import ProjectInfo from './ProjectInfo';
import { useZIndexManager } from '@/lib/hooks/useZIndexManager';
import { useCollapseNavigate } from '@/lib/hooks/useCollapseNavigate';
import { useWheelPageNavigation } from '@/lib/hooks/useWheelPageNavigation';
import { ROUTES } from '@/lib/routes';
import { PROJECT_THUMBNAIL_ASPECT_RATIO } from '@/lib/constants/media';
import { getWindowSize, VIDEO_CONTROLS_HEIGHT } from '@/lib/constants/window';
import { getMediaKind } from '@/lib/utils/mediaKind';

interface Props {
  project: ProjectModel;
  prevProjectHref: string;
  nextProjectHref: string;
  presetIndex: number;
}

interface Position {
  x: number;
  y: number;
}

interface PresetSet {
  projectInfoPosition: Position;
  featuredVideoPosition: Position;
  featuredImagePosition: Position;
  draggableWindowPositions: Position[];
}

const WHEEL_NAVIGATION_COOLDOWN_MS = 2000;

const FALLBACK_POSITION: Position = { x: 0, y: 0 };

export default function ProjectDetail({ project, prevProjectHref, nextProjectHref, presetIndex }: Props) {
  const { width: widthContent, height: heightContent } = useContentSize();
  const { getZIndex, handleElementClick } = useZIndexManager();
  const { isCollapsing, navigate: collapseAndNavigate } = useCollapseNavigate();

  const draggableWindowSize = getWindowSize(widthContent, 0.234, PROJECT_THUMBNAIL_ASPECT_RATIO);
  const featuredImageSize = getWindowSize(widthContent, 0.5, PROJECT_THUMBNAIL_ASPECT_RATIO);
  const videoPlayerSize = getWindowSize(widthContent, 0.5, PROJECT_THUMBNAIL_ASPECT_RATIO);
  const projectInfoSize = {
    width: Math.floor(widthContent * 0.28),
    height: 200,
  };
  const presets = useMemo<PresetSet[] | null>(() => {
    if (widthContent <= 0 || heightContent <= 0) return null;
    return [
      {
        projectInfoPosition: { x: window.innerWidth * 0.04, y: window.innerHeight * 0.05 },
        featuredVideoPosition: { x: window.innerWidth * 0.5 - videoPlayerSize.width / 2, y: window.innerHeight * 0.50 - videoPlayerSize.height / 2 - VIDEO_CONTROLS_HEIGHT },
        featuredImagePosition: { x: window.innerWidth * 0.5 - featuredImageSize.width / 2, y: window.innerHeight * 0.5 - featuredImageSize.height / 2 },
        // This NEEDS to be an exhaustive list, and accessors should NEVER access out of bounds.
        draggableWindowPositions: [
          { x: window.innerWidth * 0.66, y: window.innerHeight * 0.07 },
          { x: window.innerWidth * 0.02, y: window.innerHeight * 0.5 },
          { x: window.innerWidth * 0.20, y: window.innerHeight * 0.7 },
          { x: window.innerWidth * 0.74, y: window.innerHeight * 0.62 },
        ],
      },
      {
        projectInfoPosition: { x: window.innerWidth * 0.06, y: window.innerHeight * 0.02 },
        featuredVideoPosition: { x: window.innerWidth * 0.5 - videoPlayerSize.width / 2, y: window.innerHeight * 0.50 - videoPlayerSize.height / 2 - VIDEO_CONTROLS_HEIGHT },
        featuredImagePosition: { x: window.innerWidth * 0.5 - featuredImageSize.width / 2, y: window.innerHeight * 0.5 - featuredImageSize.height / 2 },
        // This NEEDS to be an exhaustive list, and accessors should NEVER access out of bounds.
        draggableWindowPositions: [
          { x: window.innerWidth * 0.70, y: window.innerHeight * 0.05 },
          { x: window.innerWidth * 0.764, y: window.innerHeight * 0.42 },
          { x: window.innerWidth * 0.71, y: window.innerHeight * 0.70 },
          { x: window.innerWidth * 0.03, y: window.innerHeight * 0.60 },
        ],
      },
    ];
  }, [widthContent, heightContent, videoPlayerSize.width, videoPlayerSize.height, featuredImageSize.width, featuredImageSize.height]);

  useWheelPageNavigation({
    onPrev: () => collapseAndNavigate(prevProjectHref),
    onNext: () => collapseAndNavigate(nextProjectHref),
    cooldownMs: WHEEL_NAVIGATION_COOLDOWN_MS,
  });

  // Assumes draggable's pivot point is at top left
  const constrainDraggableToViewport = (pos: { x: number; y: number }, size: { width: number; height: number }) => {
    const constrainedX = Math.max(-size.width / 2, Math.min(window.innerWidth - size.width / 2, pos.x));
    const constrainedY = Math.max(-size.height / 2, Math.min(window.innerHeight - size.height / 2, pos.y));
    return { x: constrainedX, y: constrainedY };
  };

  const featuredMedia: string | undefined = project.projectMedia[0];

  return (
    <div
      className={styles['darken']}
      onClick={() => collapseAndNavigate(ROUTES.home)}
    >
      {project.projectMedia
        .filter((_, index) => index >= 1)
        .map((mediaSrc: string, index: number) => {
          const id = `project-${index}`;
          return (
            <DraggableWindow
              key={`${project.projectTitle}-${index}`}
              title={formatDesktopWindowTitle(project.projectTitle, index + 2)}
              src={mediaSrc}
              initialPosition={presets?.[presetIndex]?.draggableWindowPositions[index] ?? FALLBACK_POSITION}
              size={draggableWindowSize}
              mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
              allowDragging
              constrainingFunction={constrainDraggableToViewport}
              animationDelay={0.2 + index * 0.1}
              autoplay
              zIndex={getZIndex(id)}
              onPointerDownCapture={() => handleElementClick(id)}
              widthContent={widthContent}
              isCollapsing={isCollapsing}
            />
          )
        })
      }

      <ProjectInfo
        key={`${project.projectTitle}-info`}
        title={formatDesktopWindowTitle(project.projectTitle, project.projectMedia.length + 1)}
        client={project.clientName}
        services={project.services}
        description={project.projectDescription}
        initialPosition={presets?.[presetIndex]?.projectInfoPosition ?? FALLBACK_POSITION}
        size={projectInfoSize}
        constrainingFunction={constrainDraggableToViewport}
        animationDelay={0.1}
        zIndex={getZIndex('projectInfo')}
        onPointerDownCapture={() => handleElementClick('projectInfo')}
        widthContent={widthContent}
        isCollapsing={isCollapsing}
      />

      {featuredMedia && (getMediaKind(featuredMedia) === 'video' ? (
        <VideoPlayer
          key={`${project.projectTitle}-video`}
          title={formatDesktopWindowTitle(project.projectTitle, 1)}
          src={featuredMedia}
          initialPosition={presets?.[presetIndex]?.featuredVideoPosition ?? FALLBACK_POSITION}
          size={videoPlayerSize}
          mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
          allowDragging
          constrainingFunction={constrainDraggableToViewport}
          zIndex={getZIndex('featuredMedia')}
          onPointerDownCapture={() => handleElementClick('featuredMedia')}
          prevProjectHref={prevProjectHref}
          nextProjectHref={nextProjectHref}
          widthContent={widthContent}
          isCollapsing={isCollapsing}
          onNavigate={collapseAndNavigate}
        />
      ) : (
        <DraggableWindow
          key={`${project.projectTitle}-featured`}
          title={formatDesktopWindowTitle(project.projectTitle, 1)}
          src={featuredMedia}
          initialPosition={presets?.[presetIndex]?.featuredImagePosition ?? FALLBACK_POSITION}
          size={featuredImageSize}
          mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
          allowDragging
          constrainingFunction={constrainDraggableToViewport}
          zIndex={getZIndex('featuredMedia')}
          onPointerDownCapture={() => handleElementClick('featuredMedia')}
          prevProjectHref={prevProjectHref}
          nextProjectHref={nextProjectHref}
          widthContent={widthContent}
          isCollapsing={isCollapsing}
          onNavigate={collapseAndNavigate}
          eager
        />
      ))}
    </div>
  )
}
